import { getUserIdFromToken, getSessionId, setSessionId, isLoggedIn, generateSessionId, getGoalLevel } from "@/utils/storage";
import { getToken } from "@/utils/storage";
import { startGuestExam, getStoredGuestExamUser, startPaidExam } from "@/api/exam";
import { playTrackedLiveExamAudio } from "@/lib/speaking/exam/live/liveExamRuntimeAudio";
import {
  isLiveExamRuntimeExiting,
  invokeUnlessLiveExamExiting,
} from "@/lib/speaking/exam/live/liveExamRuntimeTeardown";
import {
  tryConsumeTask3NudgeAllowance,
  TASK3_WS_KEEPALIVE_INTERVAL_MS,
} from "@/lib/speaking/exam/live/task3NudgePolicy";
import {
  canReleaseHeldNudgeAt,
  canStartNudgePrefetch,
  computeAudibleReleaseAtMs,
  shouldSendImmediateNudgeAtSixSeconds,
  TASK3_DEFAULT_BACKEND_AUDIO_LATENCY_MS,
  updateRollingBackendLatencyEstimateMs,
} from "@/lib/speaking/exam/live/task3NudgePrefetchPolicy";
import {
  computeLatencyMs,
  markSpeakingLatency,
} from "@/lib/speaking/exam/live/speakingLatencyTelemetry";

export interface AudioServiceConfig {
  websocketUrl: string;
  sessionId?: string;
}
declare global {
  interface Window {
    __waitingForEndSound?: boolean;
    navigateToDashboard?: () => void;
  }
}

export interface AudioServiceCallbacks {
  onTranscriptUpdate?: (transcript: string) => void;
  onLiveTranscriptUpdate?: (transcript: string) => void;
  onAIResponse?: (response: { text: string; audio?: string }) => void;
  onStatusChange?: (status: string) => void;
  onConnectionChange?: (connected: boolean) => void;
  onError?: (error: string) => void;
  onAIQuestionDisplay?: (question: string) => void;
  onAIQuestionHide?: () => void;
  onSilenceDetected?: () => void;
  onUserSpeaking?: () => void;
  onUserStoppedSpeaking?: () => void;
  onMicActivate?: () => void;
  onMicDeactivate?: () => void;
  onEndMessageFinished?: () => void;
  onTopicDisplay?: (topic: string) => void;
  // New: deliver full Task 3 topic payload (including audio) to UI
  onTopicReceived?: (payload: { text: string; audio?: string; topicId?: string }) => void;
  // UI should play predefined end (task_completed) audio
  onPredefinedEndRequested?: () => void;
  onUploadComplete?: (publicUrl: string, path: string) => void;
}

class AudioService {
  private websocket: WebSocket | null = null;
  // private mediaRecorder: MediaRecorder | null = null;
  private audioStream: MediaStream | null = null;
  private isRecording = false;
  private sessionId: string | null = null;
  private config: AudioServiceConfig;
  private callbacks: AudioServiceCallbacks;
  private reconnectAttempts = 0;
  private shouldReconnect = true;
  private fullTranscript = "";

  private liveTranscript = "";
  // private recordedChunks: Blob[] = [];

  // Silence / speech tracking (Task 3)
  private lastSpeechTime = 0; // epoch ms of last speech/interim/final update
  private silenceInterval: NodeJS.Timeout | null = null;

  // AI and playback state
  private isAISpeaking = false;
  private aiResponsePending = false;

  // Message queue and retry mechanism
  private messageQueue: Array<{ message: any, retryCount: number, maxRetries: number, timestamp: number }> = [];
  private isProcessingQueue = false;
  private uploadCompletePending = false;
  private questionInFlight = false;
  private suppressAIResponsesUntil = 0;
  private aiGenerationWatchdogTimer: NodeJS.Timeout | null = null;
  private aiGenerationAttempts = 0;
  private MAX_AI_GENERATION_ATTEMPTS = 2;
  private currentAudioElement: HTMLAudioElement | null = null;
  private audioContext: AudioContext | null = null;
  private currentAudioSource: AudioBufferSourceNode | null = null;

  private remainingSeconds = 120;
  private endMessageSpoken = false;
  private endingTriggered = false;
  private currentAIText: string = "";
  private aiStartTime: number = 0;
  private userHasEverSpoken = false;
  private isPlayingStaticMessage = false;
  private currentStaticMessageType: string | null = null;
  private isProtectedStaticMessage = false;
  private cancelAllAI = false;

  // Task-specific extras
  private totalDuration: number = 0;
  /** Monotonic: once true for this attempt, never reset until new attempt lifecycle. */
  private nudgeAllowanceConsumed: boolean = false;
  private isPlayingNudge: boolean = false;
  private nudgePending: boolean = false; // NEW: Track when waiting for backend nudge response
  private nudgeTimeout: NodeJS.Timeout | null = null; // NEW: Timeout for nudge response
  private endTriggeredSource: string | null = null;
  private taskCreatedConfirmed: boolean = false;
  private stopInProgress: boolean = false;
  // Track whether server confirmed authentication for this session
  private isAuthenticated: boolean = false;
  private serverUserId: string | null = null;
  private hasConnectedOnce = false;
  private topicRequested = false;
  private topicReceived = false;
  private lastTopicRequest: { preferredTopicId?: string | null; difficultyLevel?: string | null } | null = null;

  private shutdownRequested = false;
  private isSocketClosed = false;
  private connectionInProgress = false;
  private wsKeepaliveInterval: ReturnType<typeof setInterval> | null = null;

  // Task 3 nudge prefetch / hold (latency-compensated audible @ 6s silence)
  private silenceStartedAtMs = 0;
  private nudgePrefetchStarted = false;
  private nudgePrefetchSentAtMs = 0;
  private nudgeActiveRequestGeneration = 0;
  private heldNudgeBuffer: AudioBuffer | null = null;
  private heldNudgeText = "";
  private heldNudgeGeneration = 0;
  private audibleReleaseAtMs: number | null = null;
  private nudgeReleaseTimer: ReturnType<typeof setTimeout> | null = null;
  private estimatedBackendAudioLatencyMs = TASK3_DEFAULT_BACKEND_AUDIO_LATENCY_MS;

  constructor(config: AudioServiceConfig, callbacks: AudioServiceCallbacks = {}) {
    this.config = config;
    this.callbacks = callbacks;
  }

  // Expose the underlying MediaStream so other services (eg Deepgram) can reuse it
  public getMediaStream(): MediaStream | null {
    return this.audioStream;
  }

  // Determine if this is a guest session (guest sessions should keep the WebSocket open)
  private isGuestSession(): boolean {
    // Primary: sessionId prefix
    if (this.sessionId && this.sessionId.startsWith('guest_')) return true;
    // Fallbacks: guest token or stored guest user
    try {
      const guestToken = localStorage.getItem('guestToken');
      if (guestToken) return true;
    } catch { }
    try {
      const rawGuest = localStorage.getItem('guest_user');
      if (rawGuest) return true;
    } catch { }
    return false;
  }

  private finalizeDisconnectForGuest(reason: string) {
    if (this.stopInProgress) { console.log('⏹️ Stop already in progress, skip duplicate close'); return; }
    this.stopInProgress = true;
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      try {
        this.websocket.send(JSON.stringify({
          type: 'end_session',
          sessionId: this.sessionId,
          timestamp: Date.now(),
          finalTranscript: this.fullTranscript,
          reason
        }));
      } catch { }
      setTimeout(() => {
        try {
          this.websocket?.close(1000, 'Session ended');
          this.websocket = null;
          console.log('✅ WebSocket disconnected (guest-controlled)');
        } catch (error) {
          console.error('❌ Error closing WebSocket:', error);
        }
      }, 100);
    }
  }

  public async unlockAudio() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
  }

  private resetTask3NudgeStateForNewAttempt(): void {
    this.nudgeAllowanceConsumed = false;
    this.nudgePending = false;
    this.isPlayingNudge = false;
    this.cancelTask3NudgePrefetchAndHold("new attempt");
    if (this.nudgeTimeout) {
      clearTimeout(this.nudgeTimeout);
      this.nudgeTimeout = null;
    }
    this.userHasEverSpoken = false;
    this.lastSpeechTime = 0;
    this.silenceStartedAtMs = 0;
    this.estimatedBackendAudioLatencyMs = TASK3_DEFAULT_BACKEND_AUDIO_LATENCY_MS;
  }

  private clearNudgeReleaseTimer(): void {
    if (this.nudgeReleaseTimer) {
      clearTimeout(this.nudgeReleaseTimer);
      this.nudgeReleaseTimer = null;
    }
  }

  /** Discard in-flight/held nudge without consuming allowance (user resumed or quit). */
  public cancelTask3NudgePrefetchAndHold(reason: string): void {
    this.nudgeActiveRequestGeneration += 1;
    this.nudgePrefetchStarted = false;
    this.nudgePending = false;
    this.heldNudgeBuffer = null;
    this.heldNudgeText = "";
    this.audibleReleaseAtMs = null;
    this.clearNudgeReleaseTimer();
    if (this.nudgeTimeout) {
      clearTimeout(this.nudgeTimeout);
      this.nudgeTimeout = null;
    }
    console.log(`🔄 Task 3 nudge prefetch/hold cancelled (${reason})`);
  }

  private beginTask3SilencePeriod(nowMs: number): void {
    this.silenceStartedAtMs = nowMs;
    this.audibleReleaseAtMs = computeAudibleReleaseAtMs(nowMs);
    this.nudgePrefetchStarted = false;
    markSpeakingLatency({
      task: 'task3',
      marker: 'T3_SILENCE_STARTED',
      sessionId: this.sessionId ?? undefined,
    });
  }

  private startNudgePrefetchRequest(): void {
    if (this.nudgeAllowanceConsumed || this.nudgePrefetchStarted || this.nudgePending) {
      return;
    }
    if (isLiveExamRuntimeExiting() || this.shutdownRequested) {
      return;
    }

    this.nudgePrefetchStarted = true;
    this.nudgeActiveRequestGeneration += 1;
    const generation = this.nudgeActiveRequestGeneration;
    this.nudgePrefetchSentAtMs = markSpeakingLatency({
      task: 'task3',
      marker: 'T3_NUDGE_PREFETCH_SENT',
      sessionId: this.sessionId ?? undefined,
      metrics: {
        prefetch_delay_ms: computeLatencyMs(this.silenceStartedAtMs, Date.now()),
      },
    });

    console.log("💬 Task 3: prefetching nudge (held until 6s silence)");
    this.nudgePending = true;
    this.sendWebSocketMessage({ type: 'nudge', clientGeneration: generation });

    this.nudgeTimeout = setTimeout(() => {
      if (this.nudgePending) {
        console.log("⏰ Nudge timeout - backend didn't respond within 10s");
        this.nudgePending = false;
      }
    }, 10000);
  }

  private scheduleHeldNudgeRelease(): void {
    this.clearNudgeReleaseTimer();
    if (!this.audibleReleaseAtMs || !this.heldNudgeBuffer) return;
    const delayMs = Math.max(0, this.audibleReleaseAtMs - Date.now());
    this.nudgeReleaseTimer = setTimeout(() => {
      this.tryReleaseHeldNudge(Date.now());
    }, delayMs);
  }

  private tryReleaseHeldNudge(nowMs: number): void {
    if (
      !canReleaseHeldNudgeAt({
        nowMs,
        audibleReleaseAtMs: this.audibleReleaseAtMs ?? nowMs,
        hasHeldAudio: !!this.heldNudgeBuffer,
      })
    ) {
      return;
    }
    if (isLiveExamRuntimeExiting() || this.shutdownRequested) {
      this.cancelTask3NudgePrefetchAndHold("runtime exit");
      return;
    }
    if (!tryConsumeTask3NudgeAllowance(this)) {
      this.heldNudgeBuffer = null;
      return;
    }

    const buffer = this.heldNudgeBuffer;
    const text = this.heldNudgeText;
    this.heldNudgeBuffer = null;
    this.heldNudgeText = "";
    this.clearNudgeReleaseTimer();

    const heldUntilMs = computeLatencyMs(this.nudgePrefetchSentAtMs, nowMs);
    const playbackStartedMs = markSpeakingLatency({
      task: 'task3',
      marker: 'T3_NUDGE_PLAYBACK_STARTED',
      sessionId: this.sessionId ?? undefined,
      metrics: {
        held_until_policy_ms: Math.max(0, nowMs - (this.audibleReleaseAtMs ?? nowMs)),
        total_silence_to_audible_ms: computeLatencyMs(this.silenceStartedAtMs, nowMs),
        prefetch_delay_ms: computeLatencyMs(this.silenceStartedAtMs, this.nudgePrefetchSentAtMs),
        backend_response_ms: heldUntilMs,
      },
    });

    void this.playHeldNudgeBuffer(buffer!, text, playbackStartedMs);
  }

  private async playHeldNudgeBuffer(buffer: AudioBuffer, aiText: string, playbackStartedMs: number) {
    if (isLiveExamRuntimeExiting() || this.shutdownRequested) return;
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }
      if (isLiveExamRuntimeExiting()) return;

      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 1.0;
      source.connect(gainNode).connect(this.audioContext.destination);
      this.currentAudioSource = source;
      this.isPlayingNudge = true;
      this.currentAIText = aiText;

      source.onended = () => {
        if (this.cancelAllAI) return;
        this.isAISpeaking = false;
        this.aiResponsePending = false;
        this.isPlayingNudge = false;
        this.currentAudioSource = null;
        if (aiText) {
          this.sendWebSocketMessage({
            type: 'assistant_spoken',
            text: aiText,
            wasInterrupted: false,
            timestamp: Date.now(),
            duration: Math.max(0, Date.now() - playbackStartedMs),
          });
        }
      };

      source.start(0);
      this.isAISpeaking = true;
      this.aiResponsePending = true;
      this.aiStartTime = playbackStartedMs;
      this.cancelAllAI = false;
      this.callbacks.onStatusChange?.("AI is speaking...");
      this.callbacks.onAIResponse?.({ text: aiText });
      this.callbacks.onAIQuestionDisplay?.(aiText);
    } catch (err) {
      console.error("❌ Held nudge playback failed:", err);
      this.isPlayingNudge = false;
      this.isAISpeaking = false;
      this.aiResponsePending = false;
    }
  }

  private async handleNudgeBackendResponse(response: { text?: string; audio?: string }) {
    const generation = this.nudgeActiveRequestGeneration;
    const responseReceivedMs = markSpeakingLatency({
      task: 'task3',
      marker: 'T3_NUDGE_RESPONSE_RECEIVED',
      sessionId: this.sessionId ?? undefined,
      metrics: {
        backend_response_ms: computeLatencyMs(this.nudgePrefetchSentAtMs, Date.now()),
      },
    });

    if (this.nudgeTimeout) {
      clearTimeout(this.nudgeTimeout);
      this.nudgeTimeout = null;
    }
    this.nudgePending = false;

    if (generation !== this.nudgeActiveRequestGeneration) {
      console.log("🚫 Discarding stale nudge response (user resumed or cancelled)");
      return;
    }
    if (!response.audio) return;

    this.estimatedBackendAudioLatencyMs = updateRollingBackendLatencyEstimateMs(
      this.estimatedBackendAudioLatencyMs,
      computeLatencyMs(this.nudgePrefetchSentAtMs, responseReceivedMs),
    );

    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const audioData = Uint8Array.from(atob(response.audio), c => c.charCodeAt(0)).buffer;
      const buffer = await this.audioContext.decodeAudioData(audioData);
      if (generation !== this.nudgeActiveRequestGeneration || isLiveExamRuntimeExiting()) {
        return;
      }

      markSpeakingLatency({
        task: 'task3',
        marker: 'T3_NUDGE_AUDIO_READY',
        sessionId: this.sessionId ?? undefined,
        metrics: {
          audio_startup_ms: computeLatencyMs(responseReceivedMs, Date.now()),
        },
      });

      this.heldNudgeBuffer = buffer;
      this.heldNudgeText = response.text ?? "";
      this.heldNudgeGeneration = generation;
      this.scheduleHeldNudgeRelease();
      this.tryReleaseHeldNudge(Date.now());
    } catch (err) {
      console.error("❌ Failed to prepare held nudge audio:", err);
    }
  }

  // Inform backend that the task started (optional helper)
  // public startTask(payload: { task?: string; duration?: number }) {
  //   try {
  //     this.totalDuration = payload?.duration || this.remainingSeconds;
  //     this.remainingSeconds = this.totalDuration;
  //     this.sendWebSocketMessage({
  //       type: 'start_task',
  //       task: payload?.task || 'task3',
  //       duration: this.totalDuration,
  //       timestamp: Date.now()
  //     });
  //   } catch (err) {
  //     console.warn('startTask failed', err);
  //   }
  // }

  // === Audio playback helpers (AudioContext-based, Safari-friendly) ===
  private async playBase64Audio(base64Audio: string, aiText?: string, onEnd?: () => void, onStart?: () => void) {
    try {
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      const audioData = Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0)).buffer;
      const buffer = await this.audioContext.decodeAudioData(audioData);

      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;

      // Add GainNode for volume control
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 1.0; // Normal volume

      source.connect(gainNode).connect(this.audioContext.destination);

      this.currentAudioSource = source;

      source.onended = () => {
        // Check if audio was cancelled before processing the end event
        if (this.cancelAllAI) {
          console.log("🚫 Audio onended ignored - audio was cancelled");
          return;
        }

        console.log("✅ Audio finished playing");
        this.isAISpeaking = false;
        this.aiResponsePending = false;
        this.currentAudioElement = null;
        const endTime = Date.now();
        const duration = Math.max(0, endTime - this.aiStartTime);
        this.aiStartTime = 0;
        this.currentAudioSource = null;

        // Send assistant_spoken only for normal (non-static) AI responses
        if (aiText && !this.isPlayingStaticMessage) {
          this.sendWebSocketMessage({
            type: 'assistant_spoken',
            text: aiText,
            wasInterrupted: false,
            timestamp: endTime,
            duration: duration
          });

          // Mark nudge as fully played (keep nudgeSent = true since it completed successfully)
          if (this.isPlayingNudge) {
            console.log("🎯 Nudge fully played - audio completed successfully");
            this.isPlayingNudge = false;
          }
        }

        if (onEnd) onEnd();
      };

      source.start(0);
      if (onStart) onStart(); // Trigger onStart callback immediately after start()
      this.isAISpeaking = true;
      this.aiResponsePending = true;
      this.aiStartTime = Date.now();
      this.cancelAllAI = false; // Reset cancellation flag for new audio
      this.callbacks.onStatusChange?.("AI is speaking...");
    } catch (error) {
      console.error("❌ TTS playback error:", error);
      this.isAISpeaking = false;
      this.aiResponsePending = false;
      this.aiStartTime = 0;
      this.currentAudioSource = null;
    }
  }

  //added new for iphone

  public async playStaticAudioUrl(url: string, onEnd?: () => void): Promise<void> {
    if (isLiveExamRuntimeExiting()) return;
    try {
      // Mark this playback as protected static audio so speech detection
      // and cancelAIAudio won't interrupt it.
      this.isPlayingStaticMessage = true;
      this.isProtectedStaticMessage = true;
      this.currentStaticMessageType = 'static_url';

      const result = await playTrackedLiveExamAudio({
        url,
        audioContext: this.audioContext,
        onEnd: () => {
          this.isPlayingStaticMessage = false;
          this.isProtectedStaticMessage = false;
          this.currentStaticMessageType = null;
          try { this.callbacks.onStatusChange?.("Listening..."); } catch { }
          try { this.callbacks.onEndMessageFinished?.(); } catch { }
          if (onEnd) onEnd();
        },
      });
      if (result === 'aborted' || result === 'failed') {
        this.isPlayingStaticMessage = false;
        this.isProtectedStaticMessage = false;
        this.currentStaticMessageType = null;
      }
    } catch (err) {
      this.isPlayingStaticMessage = false;
      this.isProtectedStaticMessage = false;
      this.currentStaticMessageType = null;
      if (isLiveExamRuntimeExiting()) return;
      invokeUnlessLiveExamExiting(onEnd);
    }
  }

  // === remaining-seconds / final-phase logic ===
  setRemainingSeconds(seconds: number) {
    this.remainingSeconds = seconds;

    // Enter final-phase behavior when <= 45 seconds remain
    if (this.remainingSeconds <= 45 && !this.endingTriggered) {
      console.log("⏳ Final 45s: disabling new nudges/questions");
      this.questionInFlight = false;
      this.clearAIGenerationWatchdog();
      this.callbacks.onAIQuestionHide?.();
      this.endingTriggered = true;
    }
  }

  // The central silence monitor called periodically while recording
  private checkSilence() {
    const now = Date.now();
    const elapsedMs = Math.max(0, now - this.lastSpeechTime);
    const elapsed = elapsedMs / 1000;

    if (!this.userHasEverSpoken) {
      return;
    }

    if (elapsed >= 1) {
      console.log("🔍 Silence check:", {
        elapsed: elapsed.toFixed(1) + "s",
        nudgeAllowanceConsumed: this.nudgeAllowanceConsumed,
        nudgePending: this.nudgePending,
        nudgePrefetchStarted: this.nudgePrefetchStarted,
        heldNudge: !!this.heldNudgeBuffer,
        remainingSeconds: this.remainingSeconds,
      });
    }

    if (
      canStartNudgePrefetch({
        elapsedSilenceMs: elapsedMs,
        estimatedBackendLatencyMs: this.estimatedBackendAudioLatencyMs,
        prefetchAlreadyStarted: this.nudgePrefetchStarted,
        nudgeAllowanceConsumed: this.nudgeAllowanceConsumed,
        userHasEverSpoken: this.userHasEverSpoken,
        remainingSeconds: this.remainingSeconds,
      })
    ) {
      this.startNudgePrefetchRequest();
    }

    this.tryReleaseHeldNudge(now);

    if (
      shouldSendImmediateNudgeAtSixSeconds({
        elapsedSilenceMs: elapsedMs,
        nudgeAllowanceConsumed: this.nudgeAllowanceConsumed,
        prefetchStarted: this.nudgePrefetchStarted,
        nudgeRequestInFlight: this.nudgePending,
        userHasEverSpoken: this.userHasEverSpoken,
        remainingSeconds: this.remainingSeconds,
      })
    ) {
      this.startNudgePrefetchRequest();
    }
  }

  // Guarded end trigger — ensures only one end is sent/played
  public triggerEndOnce(source: string = "unknown"): void {
    if (isLiveExamRuntimeExiting()) {
      return;
    }
    if (this.endMessageSpoken || this.isPlayingStaticMessage) {
      console.log(`⚠️ End already triggered or static message playing, ignoring trigger from: ${source}`);
      return;
    }
    this.endMessageSpoken = true;
    this.endTriggeredSource = source;
    // Ask UI to play predefined end audio and perform cleanup/navigation
    try {
      this.callbacks.onPredefinedEndRequested?.();
    } catch { }
  }

  // Deprecated: no longer used; UI uses predefined audio for intro/start/end
  // sendStaticMessage(messageType: string): void {}

  // Request Task 3 topic from backend
  public requestTask3Topic(preferredTopicId?: string | null, difficultyLevel?: string | null): void {
    try {
      this.topicRequested = true;
      this.topicReceived = false;
      this.lastTopicRequest = { preferredTopicId, difficultyLevel };
      console.log("🎯 Task 3: Requesting topic with preferredTopicId:", preferredTopicId, "and difficultyLevel:", difficultyLevel);
      const userId = getUserIdFromToken() || getStoredGuestExamUser()?.user_id || null;
      const msg: any = {
        type: 'generate_task3_topic',
        timestamp: Date.now(),
        user_id: userId,
        session_id: this.sessionId,
        task: 'task3',
        preferredTopicId: preferredTopicId || undefined,
        difficultyLevel: difficultyLevel || undefined,
      };
      console.log("🎯 Task 3: Sending message:", msg);
      // If we have an auth token or a JWT-derived userId, wait for server to confirm auth
      const hasLocalToken = isLoggedIn() || !!localStorage.getItem('guestToken') || !!localStorage.getItem('guestExamUser');
      console.log("🎯 Task 3: Has local token:", hasLocalToken);
      (async () => {
        if (hasLocalToken) {
          try {
            await this.waitForAuthOk(1500);
          } catch (e) {
            console.warn('Proceeding to request topic without auth_ok (timeout):', e);
          }
        }
        console.log("🎯 Task 3: before sent:");
        markSpeakingLatency({
          task: 'task3',
          marker: 'T3_TOPIC_REQUEST_SENT',
          sessionId: this.sessionId ?? undefined,
        });
        this.sendWebSocketMessage(msg);
      })();
    } catch (err) {
      console.error("🎯 Task 3: Error requesting topic:", err);
      // Fallback to minimal message if something goes wrong
      this.sendWebSocketMessage({ type: 'generate_task3_topic', timestamp: Date.now() });
    }
  }

  // Wait for server to send 'auth_ok' (or until timeout)
  private waitForAuthOk(timeoutMs: number = 1000): Promise<void> {
    if (this.isAuthenticated) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const onAuth = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        // remove listener by no-op - we can't easily remove since we don't register one,
        // but resolve with rejection to indicate timeout
        reject(new Error('auth_ok timeout'));
      }, timeoutMs);

      // Polling fallback: check every 50ms until authenticated or timeout
      const poll = setInterval(() => {
        if (this.isAuthenticated) {
          clearInterval(poll);
          clearTimeout(timer);
          resolve();
        }
      }, 50);
    });
  }

  // === connection lifecycle ===
  async testConnection(): Promise<boolean> {
    try {
      console.log('🧪 Testing WebSocket connection...');
      await this.connectWebSocket();
      console.log('✅ WebSocket connection test successful!');
      return true;
    } catch (error) {
      console.error('❌ WebSocket connection test failed:', error);
      return false;
    }
  }

  getConnectionStatus(): { connected: boolean; readyState: number | null } {
    return {
      connected: this.websocket?.readyState === WebSocket.OPEN,
      readyState: this.websocket?.readyState || null
    };
  }

  async initialize(): Promise<boolean> {
    try {
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      this.callbacks.onStatusChange?.("🎙️ Microphone access granted");
      return true;
    } catch (error) {
      console.error('Failed to access microphone:', error);
      this.callbacks.onError?.("❌ Microphone access denied");
      return false;
    }
  }

  //added New: Initialize or reuse session for Task 3
  async initializeSession(): Promise<void> {
    try {
      if (isLoggedIn()) {
        // Paid user flow: use existing sessionId from localStorage (created in Task1)
        console.log('🔐 Initializing paid user session for Task 3...');

        // Check for existing session ID - prioritize current_exam_id for retry scenarios
        const existingSessionId = getSessionId();
        const currentExamId = localStorage.getItem('current_exam_id');

        // Use current_exam_id if available (retry scenario), otherwise use session_id
        const sessionToUse = currentExamId || existingSessionId;

        if (sessionToUse) {
          // Use the existing sessionId from Task1/Task2 or retry
          this.sessionId = sessionToUse;
          console.log('✅ Using existing sessionId for Task 3:', this.sessionId,
            currentExamId ? '(from retry - current_exam_id)' : '(from session_id)');

          // For retry scenarios, don't create new session - just use existing one
          if (currentExamId) {
            console.log('🔄 RETRY MODE - Using existing session ID, no new session creation needed');
            return;
          }
        } else {
          // Fallback: create new exam if no sessionId exists (normal flow only)
          console.log('⚠️ No existing sessionId found, creating new exam for Task 3...');
          const goalLevel = getGoalLevel();

          // Generate session ID in frontend first
          this.sessionId = 'exam_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
          setSessionId(this.sessionId);
          console.log('🎯 Generated new session ID for Task 3:', this.sessionId);

          const response = await startPaidExam({
            goal_level: goalLevel ?? null,
            session_id: this.sessionId, // Send our generated session ID
          });

          console.log('✅ New paid user session created for Task 3:', this.sessionId);
        }
      } else {
        // Guest user flow: use existing guest_id and session_id from localStorage
        console.log('👤 Initializing guest user session for Task 3...');

        // Check for existing session ID - prioritize current_exam_id for retry scenarios
        const existingSessionId = getSessionId();
        const currentExamId = localStorage.getItem('current_exam_id');

        // Use current_exam_id if available (retry scenario), otherwise use session_id
        const sessionToUse = currentExamId || existingSessionId;

        if (sessionToUse) {
          // Use existing sessionId and guestId from Task1/Task2 or retry
          this.sessionId = sessionToUse;
          console.log('✅ Using existing sessionId and guestId for Task 3:', this.sessionId,
            currentExamId ? '(from retry - current_exam_id)' : '(from session_id)');

          // For retry scenarios, don't create new session - just use existing one
          if (currentExamId) {
            console.log('🔄 RETRY MODE - Using existing guest session ID, no new session creation needed');
            return;
          }
        } else {
          // Fallback: create new guest session if no existing data (normal flow only)
          console.log('⚠️ No existing session data found, creating new guest session for Task 3...');
          const response = await startGuestExam();
          // setGuestId(response.guest_id);
          this.sessionId = response.last_session_id;

          // Generate a unique session_id for this exam session using the same logic
          // this.sessionId = generateSessionId();
          setSessionId(this.sessionId);
          console.log('✅ New guest user session created for Task 3:', this.sessionId, 'with guest_id:',
            // response.guest_id
            response.last_session_id
          );
        }
      }
    } catch (error) {
      console.error('❌ Failed to initialize session for Task 3:', error);
      throw error;
    }
  }


  // Start recording + open websocket + start silence monitor //New added 
  async startRecording(): Promise<boolean> {
    if (this.shutdownRequested) {
      console.warn("⚠️ startRecording() called after shutdown requested - ignoring");
      return false;
    }
    if (!this.audioStream) {
      const initialized = await this.initialize();
      if (!initialized) return false;
    }

    try {
      if (this.shutdownRequested) return false;
      this.topicRequested = false;
      this.topicReceived = false;
      this.lastTopicRequest = null;
      // Initialize session if needed
      if (!this.sessionId) {
        await this.initializeSession();
      }
      if (this.shutdownRequested) return false;

      await this.connectWebSocket();

      // Just prep chunks array now (no recorder yet)
      // this.recordedChunks = [];

      // No MediaRecorder needed for Task 3 anymore (transcripts only)
      console.log("🎙️ AudioService: Recording started (stream active, no local file recording)");
      this.isRecording = true;
      this.callbacks.onStatusChange?.("🎙️ Recording started! Start speaking...");

      // Silence monitor
      if (!this.silenceInterval) {
        this.silenceInterval = setInterval(() => {
          if (this.isRecording && !this.isAISpeaking) {
            this.checkSilence();
          }
        }, 500);
      }

      return true;

    } catch (error) {
      console.error("Failed to start recording:", error);
      this.callbacks.onError?.("❌ Failed to start recording");
      return false;
    }
  }

  public requestShutdown(reason: string) {
    if (this.shutdownRequested) return;
    this.shutdownRequested = true;

    console.log(`🛑 Shutdown requested: ${reason}`);

    this.shouldReconnect = false;
    this.stopWsKeepalive();
    // If we were recording, stop the recording flag so UI updates
    this.isRecording = false;

    // Stop silence monitor immediately
    if (this.silenceInterval) {
      clearInterval(this.silenceInterval);
      this.silenceInterval = null;
    }

    this.clearAIGenerationWatchdog();
    this.cancelTask3NudgePrefetchAndHold(`shutdown: ${reason}`);
    this.cancelAIAudio(`shutdown: ${reason}`, false);

    // Stop microphone tracks immediately so the red recording dot goes away
    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => {
        try { track.stop(); } catch {}
      });
      this.audioStream = null;
    }

    // Give some time for final transcripts to arrive before closing the socket
    console.log("⏳ Waiting 3000ms for final processing before closing socket...");
    setTimeout(() => {
      this.finalizeShutdown();
    }, 3000); 
  }

  private finalizeShutdown() {
    if (this.isSocketClosed) return;
    this.isSocketClosed = true;

    console.log("🔒 Finalizing socket shutdown");

    // Close AudioContext if it exists
    try {
      const ctx: AudioContext | undefined = (window as any).__appAudioContext;
      if (ctx && typeof ctx.close === 'function') {
        ctx.close().catch(() => {});
        (window as any).__appAudioContext = undefined;
      }
    } catch (e) {
      console.warn('Error closing global audio context during shutdown:', e);
    }

    // Final socket closure with end_session message
    if (this.websocket && (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING)) {
      try {
        console.log("📤 Sending end_session with final transcript");
        this.websocket.send(JSON.stringify({
          type: 'end_session',
          sessionId: this.sessionId,
          timestamp: Date.now(),
          finalTranscript: this.fullTranscript
        }));
      } catch (e) {
        console.warn("Failed to send end_session message:", e);
      }

      // Small delay to ensure message is sent
      setTimeout(() => {
        try {
          if (this.websocket) {
            if (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING) {
              console.log("🔌 Closing WebSocket");
              this.websocket.close(1000, 'Session ended');
            } else {
              console.log(`🔌 WebSocket readyState is ${this.websocket.readyState}, skipping close()`);
            }
          }
        } catch (e) {
          console.warn("Error during websocket close:", e);
        }
        this.websocket = null;
      }, 500);
    } else {
      console.log("⚠️ WebSocket already closed or not open, skipping end_session message");
      this.websocket = null;
    }

    this.callbacks.onStatusChange?.("🛑 Session finalized");
  }


  stopRecording(): void {
    console.log("🎙️ stopRecording() called");
    this.requestShutdown("stopRecording");
  }

  private clearAIGenerationWatchdog() {
    if (this.aiGenerationWatchdogTimer) {
      clearTimeout(this.aiGenerationWatchdogTimer);
      this.aiGenerationWatchdogTimer = null;
    }
  }

  private startAIGenerationWatchdog() {
    this.clearAIGenerationWatchdog();
    this.aiGenerationAttempts = Math.min(this.aiGenerationAttempts + 1, this.MAX_AI_GENERATION_ATTEMPTS);
    this.aiGenerationWatchdogTimer = setTimeout(() => {
      if (!this.isRecording) return;
      if (!this.aiResponsePending && !this.questionInFlight) return;
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) return;
      if (this.aiGenerationAttempts <= this.MAX_AI_GENERATION_ATTEMPTS) {
        console.log('Watchdog: resending generate_question due to timeout');
        this.sendWebSocketMessage({ type: 'nudge', timestamp: Date.now() });
        this.startAIGenerationWatchdog();
      }
    }, 3000);
  }

  // === user speech handling (called from transcription service) ===
  handleUserSpeech(interimTranscript: string, finalTranscript: string) {
    // Debug logging to see what's being passed (only if there's actual speech)
    if (interimTranscript || finalTranscript) {
      console.log("🎤 handleUserSpeech called with:", {
        interimTranscript: interimTranscript || 'EMPTY',
        finalTranscript: finalTranscript || 'EMPTY',
        hasInterim: !!interimTranscript,
        hasFinal: !!finalTranscript,
        interimLength: interimTranscript?.length || 0,
        finalLength: finalTranscript?.length || 0
      });
    }

    // Block user speech if playing protected static message (start/end)
    // if (this.isProtectedStaticMessage) {
    //   console.log(`🚫 User speech blocked - protected static message (${this.currentStaticMessageType})`);
    //   return;
    // }

    // Only cancel AI audio if there's actual meaningful user speech
    const hasUserSpeech = (interimTranscript && interimTranscript.trim().length > 0) ||
      (finalTranscript && finalTranscript.trim().length > 0);

    // Additional check: ensure the speech is meaningful (not just noise or single words)
    const isMeaningfulSpeech = (text: string) => {
      if (!text || text.trim().length === 0) return false;
      const trimmed = text.trim();

      // Filter out very short or meaningless text
      if (trimmed.length < 3) return false;

      const words = trimmed.split(/\s+/).filter(Boolean);

      // Must have at least 2 words, or a single word that's substantial
      if (words.length < 2 && words[0].length < 4) return false;

      // Filter out common noise words
      const noiseWords = ['um', 'uh', 'ah', 'er', 'mm', 'hmm', 'oh', 'ok', 'okay'];
      if (words.length === 1 && noiseWords.includes(words[0].toLowerCase())) return false;

      return true;
    };

    const hasMeaningfulSpeech = isMeaningfulSpeech(interimTranscript) || isMeaningfulSpeech(finalTranscript);

    // Only log speech analysis if there's actual speech to analyze
    if (hasUserSpeech) {
      console.log("🔍 Speech analysis:", {
        hasUserSpeech,
        hasMeaningfulSpeech,
        isPlayingStaticMessage: this.isPlayingStaticMessage,
        isAISpeaking: this.isAISpeaking,
        aiResponsePending: this.aiResponsePending,
        shouldCancel: hasMeaningfulSpeech && (this.isPlayingStaticMessage || this.isAISpeaking || this.aiResponsePending)
      });
    }

    if (hasMeaningfulSpeech && (this.isPlayingStaticMessage || this.isAISpeaking || this.aiResponsePending)) {
      console.log("⏹️ User spoke meaningfully - cancelling AI audio and prioritizing user");
      this.cancelAIAudio("user spoke meaningfully", true);
      this.cancelTask3NudgePrefetchAndHold("user spoke meaningfully");
      this.endMessageSpoken = false;
      this.lastSpeechTime = Date.now();
    } else if (hasUserSpeech && !hasMeaningfulSpeech) {
      console.log("🔇 Ignoring non-meaningful speech detection:", { interimTranscript, finalTranscript });
    } else if (!hasUserSpeech) {
      console.log("🔇 No user speech detected - ignoring");
    }

    if (interimTranscript) {
      this.lastSpeechTime = Date.now();
      this.silenceStartedAtMs = 0;
      this.audibleReleaseAtMs = null;
      this.liveTranscript = interimTranscript;
      this.callbacks.onLiveTranscriptUpdate?.(interimTranscript);

      this.aiResponsePending = false;
      this.questionInFlight = false;
      this.suppressAIResponsesUntil = Date.now() + 3000;
      this.callbacks.onAIQuestionHide?.();
      this.callbacks.onUserSpeaking?.();

      this.cancelTask3NudgePrefetchAndHold("user interim speech");

      // Reset end state when user is actively speaking
      if (this.endMessageSpoken) {
        console.log("🔄 User is speaking - resetting end state");
        this.endMessageSpoken = false;
      }
    }

    if (finalTranscript) {
      this.userHasEverSpoken = true;
      const nowMs = Date.now();
      this.cancelTask3NudgePrefetchAndHold("new final transcript");
      this.lastSpeechTime = nowMs;
      this.beginTask3SilencePeriod(nowMs);

      // Reset end state when user finishes speaking
      if (this.endMessageSpoken) {
        console.log("🔄 User finished speaking - resetting end state");
        this.endMessageSpoken = false;
      }

      this.fullTranscript += finalTranscript;
      this.callbacks.onTranscriptUpdate?.(this.fullTranscript);

      //added New: send final transcript with user_stopped_speaking
      const userId = getUserIdFromToken();
      // const guestId = userId ? null : getGuestId();
      const sessionId = getSessionId();
      const goalLevel = getGoalLevel();
      const guestUser = getStoredGuestExamUser();

      // Check if this is a retry attempt
      const isRetryAttempt = localStorage.getItem('current_task_attempt_id') !== null;

      const payload = {
        type: 'transcript',
        data: finalTranscript.trim(),
        user_id: userId ?? guestUser?.user_id,
        // guest_id: guestId,
        session_id: sessionId,
        goal_level: goalLevel,
        task: 'task3',
        isRetryAttempt: isRetryAttempt,
        timestamp: Date.now(),
      };
      console.log("📤 Sending WebSocket message payload:", payload);

      this.sendWebSocketMessage(payload);

      this.callbacks.onUserStoppedSpeaking?.();
    }
  }

  // Cancel any playing AI audio (respect protected static messages)
  public cancelAIAudio(reason: string = "unknown", isUserInterruption: boolean = false) {
    if (this.isProtectedStaticMessage) {
      console.log(`🚫 Cancellation blocked - protected static message (${this.currentStaticMessageType}) must complete`);
      return;
    }

    console.log(`🛑 Cancelling AI audio - ${reason}`);
    this.cancelAllAI = true;

    // Stop AudioContext source if active (immediate cancellation)
    if (this.currentAudioSource) {
      try {
        console.log("🛑 Stopping AudioContext source immediately");
        this.currentAudioSource.stop(0); // Stop immediately at current time
        this.currentAudioSource.disconnect();
        this.currentAudioSource = null;
      } catch (error) {
        console.error('Error canceling AudioContext source:', error);
      }
    }

    // HTMLAudioElement fallback
    if (this.currentAudioElement) {
      try {
        this.currentAudioElement.pause();
        this.currentAudioElement.currentTime = 0;
        this.currentAudioElement.src = '';
        this.currentAudioElement.load();
        this.currentAudioElement = null;
      } catch (error) {
        console.error('Error canceling audio element:', error);
      }
    }

    // If AI was speaking, notify backend that assistant was interrupted
    if (this.isPlayingStaticMessage || this.isAISpeaking) {
      const currentAIText = this.currentAIText || "AI audio";
      if (this.aiStartTime > 0) {
        this.sendWebSocketMessage({
          type: 'assistant_spoken',
          text: currentAIText,
          wasInterrupted: true,
          timestamp: Date.now(),
          duration: Date.now() - this.aiStartTime
        });
      }
    }

    // Reset AI state
    this.isAISpeaking = false;
    this.aiResponsePending = false;
    this.questionInFlight = false;
    this.isPlayingStaticMessage = false;
    this.currentStaticMessageType = null;
    this.isProtectedStaticMessage = false;
    this.callbacks.onStatusChange?.("User interrupted AI - listening...");

    if (this.isPlayingNudge && isUserInterruption) {
      console.log("🔄 Nudge playback interrupted by user — no second nudge allowed");
      this.isPlayingNudge = false;
    } else if (this.isPlayingNudge && !isUserInterruption) {
      console.log("🔄 Nudge was cancelled by system - keeping nudge allowance consumed");
      this.isPlayingNudge = false;
    }

    this.aiStartTime = 0;
    this.currentAIText = "";
  }

  private stopWsKeepalive(): void {
    if (this.wsKeepaliveInterval) {
      clearInterval(this.wsKeepaliveInterval);
      this.wsKeepaliveInterval = null;
    }
  }

  private startWsKeepalive(): void {
    if (this.shutdownRequested || this.wsKeepaliveInterval) return;
    this.wsKeepaliveInterval = setInterval(() => {
      if (this.shutdownRequested || !this.isRecording) {
        this.stopWsKeepalive();
        return;
      }
      if (this.websocket?.readyState === WebSocket.OPEN) {
        try {
          this.websocket.send(JSON.stringify({ type: 'heartbeat', timestamp: Date.now() }));
        } catch {
          /* keepalive is best-effort */
        }
      }
    }, TASK3_WS_KEEPALIVE_INTERVAL_MS);
  }

  private classifyWebSocketClose(event: CloseEvent): string {
    if (this.shutdownRequested || this.isSocketClosed) return 'INTENTIONAL_SHUTDOWN';
    if (event.wasClean && (event.code === 1000 || event.code === 1001)) return 'CLIENT_OR_BACKEND_CLOSE';
    if (event.code === 1006) return 'UNEXPECTED_NETWORK_CLOSE';
    return 'RECONNECT_CANDIDATE';
  }

  // === websocket ===
  private async connectWebSocket(): Promise<void> {
    if (this.shutdownRequested) {
      console.warn("⚠️ connectWebSocket() called after shutdown requested - rejecting");
      return Promise.reject(new Error("Shutdown requested"));
    }
    if (this.connectionInProgress) {
      console.log("⏳ WebSocket connection already in progress, skipping duplicate call");
      return Promise.resolve();
    }
    if (this.websocket && (this.websocket.readyState === WebSocket.OPEN || this.websocket.readyState === WebSocket.CONNECTING)) {
      console.log(`WebSocket already ${this.websocket.readyState === WebSocket.OPEN ? 'connected' : 'connecting'}`);
      return Promise.resolve();
    }

    console.log("🎙️ connectWebSocket called from:");
    console.trace();
    this.connectionInProgress = true;

    return new Promise((resolve, reject) => {
      // Ensure we have a sessionId (rehydrate from storage if needed) before connecting
      try {
        if (!this.sessionId) {
          const sid = getSessionId();
          if (sid) {
            this.sessionId = sid;
            console.log('🔁 Rehydrated sessionId from storage before connectWebSocket:', this.sessionId);
          }
        }
      } catch (e) {
        console.warn('⚠️ Failed to rehydrate sessionId from storage:', e);
      }

      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected');
        resolve();
        return;
      }

      console.log('Attempting to connect to WebSocket:', this.config.websocketUrl);
      this.callbacks.onConnectionChange?.(false);
      this.callbacks.onStatusChange?.("Connecting to backend...");

      this.websocket = new WebSocket(this.config.websocketUrl);

      this.websocket.onopen = () => {
        this.connectionInProgress = false;
        const wasReconnect = this.hasConnectedOnce;
        this.hasConnectedOnce = true;
        console.log('Connection details:', {
          url: this.config.websocketUrl,
          sessionId: this.sessionId,
          readyState: this.websocket!.readyState
        });
        this.callbacks.onConnectionChange?.(true);
        this.callbacks.onStatusChange?.("Connected to backend");
        markSpeakingLatency({
          task: 'task3',
          marker: 'T3_WS_CONNECTED',
          sessionId: this.sessionId ?? undefined,
        });
        this.reconnectAttempts = 0;

        // Check if this is a retry attempt
        const isRetryAttempt = localStorage.getItem('current_task_attempt_id') !== null;

        // Always prioritize current_exam_id from localStorage for session restoration
        const currentExamId = localStorage.getItem('current_exam_id');
        const connectionSessionId = currentExamId || this.sessionId;

        if (currentExamId) {
          console.log('🔄 Using current_exam_id from localStorage for connection:', connectionSessionId);
          // Update the instance sessionId to match localStorage
          this.sessionId = connectionSessionId;
        }

        // Get user_id for permanent authentication (check both paid and guest)
        let userId = getUserIdFromToken() || localStorage.getItem('user_id');
        if (!userId) {
          const guestUser = getStoredGuestExamUser();
          userId = guestUser?.user_id || null;
        }
        // Determine token now so we can include it in the initial connection message
        let tokenToSend: string | null = null;
        try {
          if (isLoggedIn()) tokenToSend = getToken() || null;
          if (!tokenToSend) {
            const guestExamUserRaw = localStorage.getItem('guestExamUser');
            const guestToken = guestExamUserRaw ? (JSON.parse(guestExamUserRaw)?.token ?? null) : (localStorage.getItem('guestToken'));
            tokenToSend = guestToken || null;
          }
        } catch (e) {
          console.warn('Error reading token for connection message:', e);
        }

        const connectionMessage: any = {
          type: 'connection',
          sessionId: connectionSessionId,
          isRetryAttempt: isRetryAttempt,
          timestamp: Date.now()
        };
        // include access_token directly in connection message so backend can authenticate on restore
        if (tokenToSend) connectionMessage.access_token = tokenToSend;
        
        // Add user_id (required for permanent authentication in Task 3)
        if (userId) {
          connectionMessage.user_id = userId;
          console.log('✅ TASK 3 - Including user_id in connection message:', userId.substring(0, 8) + '...');
        } else {
          console.warn('⚠️ TASK 3 - No user_id found for connection (guest mode not supported)');
        }

        if (isRetryAttempt) {
          console.log('🔄 TASK 3 RETRY - Sending retry flag to backend for transcript clearing');
          this.resetTask3NudgeStateForNewAttempt();
        }

        console.log('📡 TASK 3 - Sending connection message:', { type: connectionMessage.type, sessionId: connectionMessage.sessionId, hasUserId: !!connectionMessage.user_id });
        this.websocket!.send(JSON.stringify(connectionMessage));

        // Also send an explicit 'auth' message after connection as a fallback
        try {
          if (tokenToSend) {
            this.websocket!.send(JSON.stringify({ type: 'auth', access_token: tokenToSend }));
          }
        } catch (e) {
          console.warn('Auth token send failed:', e);
        }

        // If we have an existing sessionId (retry/restore scenarios), explicitly request session restoration.
        try {
          if (this.sessionId) {
            this.websocket!.send(JSON.stringify({ type: 'restore_session', sessionId: this.sessionId }));
            console.log('🔁 Sent restore_session for sessionId:', this.sessionId);
          }
        } catch (e) {
          console.warn('⚠️ Failed to send restore_session:', e);
        }

        // Process any queued messages after connection is established
        if (this.messageQueue.length > 0) {
          console.log(`🔄 WebSocket reconnected, processing ${this.messageQueue.length} queued messages`);
          this.processMessageQueue();
        }

        if (wasReconnect && this.topicRequested && !this.topicReceived) {
          const preferredTopicId = this.lastTopicRequest?.preferredTopicId;
          const difficultyLevel = this.lastTopicRequest?.difficultyLevel;
          console.log("🔄 Task 3 reconnect detected before topic arrived - re-requesting topic");
          setTimeout(() => {
            this.requestTask3Topic(preferredTopicId, difficultyLevel);
          }, 250);
        }

        this.startWsKeepalive();

        resolve();
      };

      this.websocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('Received WebSocket message:', message);
          this.handleWebSocketMessage(message);
        } catch (error) {
          console.error('Raw message data:', event.data);
        }
      };

      this.websocket.onclose = (event) => {
        this.connectionInProgress = false;
        const closeKind = this.classifyWebSocketClose(event);
        console.log('Task 3 : WebSocket disconnected:', {
          closeKind,
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          sessionId: this.sessionId,
          isRecording: this.isRecording,
          shouldReconnect: this.shouldReconnect
        });
        this.callbacks.onConnectionChange?.(false);
        this.callbacks.onStatusChange?.(" Connection lost");

        if (this.shouldReconnect && this.isRecording && !this.shutdownRequested) {
          console.log('🔄 Scheduling reconnection...');
          this.scheduleReconnect();
        }
      };

      this.websocket.onerror = (error) => {
        this.connectionInProgress = false;
        console.error('Task 3 : WebSocket connection error:', error);
        console.error('Task 3 : WebSocket error details:', {
          type: error.type,
          target: error.target,
          readyState: this.websocket?.readyState,
          sessionId: this.sessionId,
          isRecording: this.isRecording
        });
        this.callbacks.onConnectionChange?.(false);
        this.callbacks.onStatusChange?.("Connection failed");
        reject(error);
      };

      setTimeout(() => {
        if (this.websocket!.readyState !== WebSocket.OPEN) {
          reject(new Error('WebSocket connection timeout'));
          console.error('Task 3 : WebSocket connection timeout');
        }
      }, 10000);
    });
  }

  private handleWebSocketMessage(message: any): void {
    console.log(`🔍 Processing message type: ${message.type}`);

    switch (message.type) {
      case 'auth_ok':
        {
          try {
            this.isAuthenticated = true;
            this.serverUserId = message.user_id || null;
            console.log('🔐 Server confirmed auth_ok for session', this.sessionId, 'user_id:', this.serverUserId);
            markSpeakingLatency({
              task: 'task3',
              marker: 'T3_AUTH_OK',
              sessionId: this.sessionId ?? undefined,
            });
            // notify UI that auth completed (optional)
            this.callbacks.onStatusChange?.('Authenticated with backend');
          } catch (e) {
            console.warn('Error handling auth_ok:', e);
          }
        }
        break;
      case 'connection':
        this.callbacks.onStatusChange?.("Backend connection established");
        break;

      case 'static_message_response':
        // Ignored: backend static messages are not used in Task 3 UI flow
        break;

      case 'ai_response':
        {
          // Block new AI responses once in final 45s (unless AI already speaking)
          if (this.remainingSeconds <= 45 && !this.isAISpeaking) {
            console.log("🚫 BLOCKED: Ignoring new AI response in final 45s");
            this.aiResponsePending = false;
            this.questionInFlight = false;
            this.clearAIGenerationWatchdog();
            this.callbacks.onAIQuestionHide?.();
            return;
          }

          if (Date.now() < this.suppressAIResponsesUntil) {
            this.aiResponsePending = false;
            this.questionInFlight = false;
            this.callbacks.onAIQuestionHide?.();
            return;
          }

          const response = message.data;
          const timeSinceLastSpeech = Date.now() - this.lastSpeechTime;
          const userRecentlySpoke = timeSinceLastSpeech < 2000;

          if (userRecentlySpoke && this.aiResponsePending) {
            this.aiResponsePending = false;
            this.callbacks.onStatusChange?.("User interrupted AI - listening...");
            return;
          }

          //added new
          this.sendWebSocketMessage({
            type: "ai_response_ack",
            sessionId: this.sessionId,
            requestId: response.requestId ?? null,

            timestamp: Date.now()
          });

          this.aiResponsePending = false;
          this.questionInFlight = false;
          this.aiGenerationAttempts = 0;
          this.clearAIGenerationWatchdog();
          console.log("🔍 AI response received:", response);
          const isHeldNudgeResponse =
            this.nudgePrefetchStarted && !this.nudgeAllowanceConsumed;
          if (response.audio && isHeldNudgeResponse) {
            void this.handleNudgeBackendResponse(response);
            break;
          }
          if (response.audio) {
            this.callbacks.onAIResponse?.(response);
            this.callbacks.onAIQuestionDisplay?.(response.text);

            this.currentAIText = response.text;
            this.aiStartTime = Date.now();
            this.playAudioResponse(response);
          } else {
            this.callbacks.onAIQuestionHide?.();
          }
        }
        break;

      case 'heartbeat':
        this.sendWebSocketMessage({
          type: 'heartbeat_ack',
          timestamp: Date.now()
        });
        break;

      //addd new
      case "ai_response_ack":
        console.log(`✅ AI response ACK received for session ${this.sessionId}, requestId: ${message.requestId}`);
        // Mark that AI response was delivered
        break;

      case 'error':
        console.error('Server error:', message.message);
        if (message.message === 'No transcript available for question generation') {
          this.callbacks.onStatusChange?.("Listening...");
          this.aiResponsePending = false;
        } else {
          this.callbacks.onStatusChange?.(` ${message.message}`);
        }
        break;

      // Handle Task 3 topic message
      case 'task3_topic':
        {
          this.topicReceived = true;
          markSpeakingLatency({
            task: 'task3',
            marker: 'T3_TOPIC_RESPONSE_RECEIVED',
            sessionId: this.sessionId ?? undefined,
          });
          const payload = message.data;
          const topicText = payload?.topic || '';
          const topicAudio = payload?.audio;
          const topicId = payload?.topicId;
          console.log("🎯 Task 3: Received topic from websocket:", topicText, topicAudio, topicId);
          // Store topicId in localStorage if received
          if (topicId) {
            try {
              localStorage.setItem('task3_topic_id', topicId);
              console.log('✅ Topic ID stored in localStorage:', topicId);
            } catch (err) {
              console.warn('⚠️ Failed to store topic ID in localStorage:', err);
            }
          }

          // Forward to UI for orchestration: intro (predefined) -> topic audio -> start_exam_3 (predefined)
          // REMOVED: if (topicText) this.callbacks.onTopicDisplay?.(topicText); - handled by onTopicReceived with onStart callback
          this.callbacks.onTopicReceived?.({ text: topicText, audio: topicAudio, topicId });
        }
        break;

      case 'upload_url_error':
        console.error('❌ Task 3 audio upload error:', message.message);
        break;
    }
  }

  // Public helper to play provided topic audio base64 and notify when finished
  public playTopicAudio(base64Audio: string, text: string, onEnd?: () => void, onStart?: () => void) {
    try {
      // Treat topic audio as a protected static message so it cannot be
      // interrupted by user speech / Deepgram interruptions.
      this.isPlayingStaticMessage = true;
      this.isProtectedStaticMessage = true;
      this.currentStaticMessageType = 'topic_audio';

      this.currentAIText = text;
      // Use playBase64Audio but clear protection after it finishes via wrapper
      this.playBase64Audio(base64Audio, text, () => {
        // Clear flags and restore listening state
        this.isPlayingStaticMessage = false;
        this.isProtectedStaticMessage = false;
        this.currentStaticMessageType = null;
        this.callbacks.onStatusChange?.("Listening...");
        this.isAISpeaking = false;
        this.aiResponsePending = false;
        this.currentAudioElement = null;
        this.lastSpeechTime = Date.now();
        this.aiStartTime = 0;
        if (onEnd) onEnd();
      }, onStart); // Pass onStart callback
      this.isAISpeaking = true;
      this.aiResponsePending = true;
      this.aiStartTime = Date.now();
      this.callbacks.onStatusChange?.("AI is speaking...");
    } catch (error) {
      console.error('Topic audio playback error:', error);
      this.isPlayingStaticMessage = false;
      this.isProtectedStaticMessage = false;
      this.currentStaticMessageType = null;
      this.isAISpeaking = false;
      this.aiResponsePending = false;
      this.currentAudioElement = null;
      this.callbacks.onStatusChange?.("Listening...");
      this.aiStartTime = 0;
      if (onEnd) onEnd();
    }
  }

  // Play normal AI audio response
  private playAudioResponse(response: any, onEnd?: () => void) {
    try {
      // Prevent multiple AI responses from being played simultaneously
      if (this.isAISpeaking || this.aiResponsePending) {
        console.log("🚫 Ignoring AI response - already playing AI audio");
        return;
      }

      const base64Audio = response.audio;
      const aiText = response.text;
      this.cancelAIAudio("playing new AI response", false);
      this.playBase64Audio(base64Audio, aiText, () => {
        this.callbacks.onStatusChange?.("Listening...");
        this.isAISpeaking = false;
        this.aiResponsePending = false;
        this.currentAudioElement = null;
        this.lastSpeechTime = Date.now();
        this.aiStartTime = 0;
        if (onEnd) onEnd();
      });
      this.isAISpeaking = true;
      this.aiResponsePending = true;
      this.aiStartTime = Date.now();
      this.callbacks.onStatusChange?.("AI is speaking...");

    } catch (error) {
      console.error('Audio playback error:', error);
      this.isAISpeaking = false;
      this.aiResponsePending = false;
      this.currentAudioElement = null;
      this.callbacks.onStatusChange?.("Listening...");
      this.aiStartTime = 0;
      if (onEnd) onEnd();
    }
  }

  private sendWebSocketMessage(message: any, maxRetries: number = 3): boolean {
    if (this.sessionId) {
      message.sessionId = this.sessionId;
    }
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, queuing message:', {
        messageType: message.type,
        readyState: this.websocket?.readyState,
        websocketExists: !!this.websocket
      });
      this.queueMessage(message, maxRetries);
      return false;
    }

    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      try {
        this.websocket.send(JSON.stringify(message));
        console.log(`✅ Message sent successfully: ${message.type}`);
        return true;
      } catch (error) {
        console.error('Failed to send WebSocket message:', error);
        this.queueMessage(message, maxRetries);
        return false;
      }
    } else {
      console.warn('WebSocket not connected, queuing message:', {
        messageType: message.type,
        readyState: this.websocket?.readyState,
        websocketExists: !!this.websocket
      });
      this.queueMessage(message, maxRetries);
      return false;
    }
  }

  private queueMessage(message: any, maxRetries: number = 3): void {
    // For critical messages like upload_complete, use higher retry count


    this.messageQueue.push({
      message,
      retryCount: 0,
      maxRetries,
      timestamp: Date.now()
    });

    console.log(`📋 Queued message: ${message.type} (max retries: ${maxRetries})`);

    // Process queue if not already processing
    if (!this.isProcessingQueue) {
      this.processMessageQueue();
    }
  }

  private async processMessageQueue(): Promise<void> {
    if (this.isProcessingQueue || this.messageQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;
    console.log(`🔄 Processing message queue (${this.messageQueue.length} messages)`);

    while (this.messageQueue.length > 0) {
      const queueItem = this.messageQueue[0];
      const { message, retryCount, maxRetries } = queueItem;

      // Check if message is too old (older than 5 minutes)
      if (Date.now() - queueItem.timestamp > 300000) {
        console.log(`⏰ Removing expired message: ${message.type}`);
        this.messageQueue.shift();
        continue;
      }

      // Try to send the message
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        try {
          this.websocket.send(JSON.stringify(message));
          console.log(`✅ Queued message sent successfully: ${message.type}`);
          this.messageQueue.shift();

          // If this was upload_complete, mark as no longer pending

        } catch (error) {
          console.error(`❌ Failed to send queued message: ${message.type}`, error);
          queueItem.retryCount++;

          if (queueItem.retryCount >= maxRetries) {
            console.error(`❌ Max retries exceeded for message: ${message.type}`);
            this.messageQueue.shift();

            // If this was upload_complete, we need to handle the failure

          } else {
            // Wait before retrying (exponential backoff)
            const delay = Math.min(1000 * Math.pow(2, queueItem.retryCount - 1), 10000);
            console.log(`⏳ Retrying ${message.type} in ${delay}ms (attempt ${queueItem.retryCount}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      } else {
        if (this.shutdownRequested) {
          console.log(`🛑 Shutdown in progress, skipping proactive reconnect for queued message: ${message.type}`);
          this.messageQueue.shift();
          continue;
        }

        // WebSocket not ready. Try to proactively reconnect while queue is non-empty.
        console.log(`⏳ WebSocket not ready, attempting reconnect for queued messages...`);
        // Try to rehydrate sessionId from storage before reconnecting so server will restore session
        try {
          const sid = getSessionId();
          if (sid && !this.sessionId) {
            this.sessionId = sid;
            console.log('🔁 Rehydrated sessionId from storage before queue-connect:', this.sessionId);
          }
        } catch (e) {
          console.warn('⚠️ Failed to rehydrate sessionId before queue reconnect:', e);
        }
        let reconnected = false;
        try {
          for (let attempt = 1; attempt <= 3; attempt++) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await this.connectWebSocket();
              if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                reconnected = true;
                console.log(`🔌 Reconnected WebSocket during queue processing (attempt ${attempt})`);
                break;
              }
            } catch (err) {
              console.warn(`🔁 Reconnect attempt ${attempt} failed while processing queue:`, err);
              // eslint-disable-next-line no-await-in-loop
              await new Promise(r => setTimeout(r, 500 * attempt));
            }
          }
        } catch (err) {
          console.warn('🔁 Error during proactive reconnect attempts:', err);
        }

        if (reconnected) {
          // go back to top of while loop to send queued messages
          continue;
        }
        // Not a critical message or fallback attempted — wait briefly and retry
        console.log('⏳ WebSocket still not ready, waiting 1s before retrying queued messages');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    this.isProcessingQueue = false;
  }



  private scheduleReconnect(): void {
    if (!this.shouldReconnect || this.shutdownRequested) return;

    this.reconnectAttempts++;
    const delay = Math.min(1500 * Math.pow(2, this.reconnectAttempts - 1), 10000);

    setTimeout(() => {
      this.connectWebSocket().catch(console.error);
    }, delay);
  }

  // Method to check if critical operations are pending before disconnection
  public canDisconnect(): boolean {


    if (this.messageQueue.length > 0) {
      console.log(`⚠️ Cannot disconnect: ${this.messageQueue.length} messages in queue`);
      return false;
    }

    return true;
  }

  // Method to force disconnect (use with caution)
  public forceDisconnect(): void {
    console.log("⚠️ Force disconnecting WebSocket");
    this.uploadCompletePending = false;
    this.messageQueue = [];
    this.finalizeShutdown();
  }

  // Immediately stop recording flag and UI state, but keep MediaRecorder, Deepgram, and websocket active
  // This allows buffered audio to continue being processed for 2-3 seconds after user clicks task end
  public stopMicrophoneImmediatelyButKeepProcessing(): void {
    console.log('🎤 TASK 3: Stopping recording flag immediately, but keeping processing active for 2-3 seconds');
    
    // Set recording flag to false (UI will show "Microphone Inactive")
    this.isRecording = false;
    
    // Cancel any playing AI audio
    this.cancelAIAudio();
    
    // Important: Do NOT stop these yet - keep them active for 2-3 seconds:
    // - audioStream (microphone tracks)
    // - websocket
    // - Deepgram (speech recognition)
    
    console.log('✅ TASK 3: Recording flag stopped, audio processing will continue briefly');
  }

  cleanup(): void {
    console.log("🧹 cleanup() called");
    this.requestShutdown("cleanup");
    this.shouldReconnect = false;
    this.hasConnectedOnce = false;
    this.sessionId = null;
    this.fullTranscript = "";
    this.liveTranscript = "";
    this.topicRequested = false;
    this.topicReceived = false;
    this.lastTopicRequest = null;

    this.cancelTask3NudgePrefetchAndHold("cleanup");
  }
}

export default AudioService;